// Shared accountability display helpers — entity-agnostic label builders + chain
// mappers used by every scope-entity surface (ChainsDrawer · ScopeChainsSection ·
// the coverage cells of the zones and clusters lists). Centralized so each
// surface renders identically and the shipped accountability components (which
// take their strings as props) get one source of t()-built labels. Per-entity status
// badges stay in each entity's xDisplay.tsx; everything here operates only on the
// wire types (ScopeChain / the person lookup's row) with `t` passed in.
// Counts go through i18next plurals — `t(key, { count })` resolves the locale's
// `_one`/`_other` forms (CONVENTIONS §i18n); never a hand-branched `count === 1 ? …`
// (banned, and it can't extend to a locale's extra CLDR forms the way i18next can).

import type { TFunction } from 'i18next';
import type { ChainPerson, ResponsibilityChainEditorLabels } from './ResponsibilityChainEditor.js';
import type { ResponsibilityCoverageLabels } from './ResponsibilityCoverage.js';
import type { ResponsibilityAssignmentItem } from './ResponsibilityAssignmentList.js';
import type { ResponsibilityAssignmentRowLabels } from './ResponsibilityAssignmentRow.js';
import type { Face } from './lookupFaces.js';
import type { ScopeChain } from '@ems-app/schemas/accountability/responses';
import type { UserLookupRow } from '@ems-app/schemas/users/lookup';

// ── chain-person mapping (a person lookup row → a person of the chain's ordered list) ─
// The chain's holders are NAMED by the person lookup's rows of given ids on the
// `accountable` rule (ADR-078 §6): the row's `covered` false → `needsScope` (naming them
// needs the companion scope grant — the editor's chip); `pickable` false → a disabled
// holder, kept in the chain and greyed (a save checks only the people it ADDS). The
// person's face gives the name and the subtitle (the role; a disabled holder's status
// word joins it — the chain's list draws no badge; a holder who will be granted scope
// wears the editor's chip alone, never its words on the subtitle too).
export function toChainPerson(row: UserLookupRow, face: Face<UserLookupRow>): ChainPerson {
  const faced = face(row);
  const needsScope = row.covered === false && row.pickable;
  return {
    id: row.id,
    name: faced.label,
    subtitle: [faced.description, needsScope ? null : faced.spoken].filter(Boolean).join(' · ') || undefined,
    needsScope,
    inactive: !row.pickable,
  };
}

// ── label builders for the shipped accountability components ────────────────────
/** The chain editor's words — `common:chain.*` (one block for every level's drawer). */
export function chainLabels(tc: TFunction): ResponsibilityChainEditorLabels {
  return {
    picker: tc('chain.picker'),
    pickerPlaceholder: tc('chain.pickerPlaceholder'),
    listLabel: tc('chain.listLabel'),
    lead: tc('chain.lead'),
    backup: tc('chain.backup'),
    empty: tc('chain.empty'),
    remove: (name) => tc('chain.remove', { name }),
    reorder: (name) => tc('reorder.handle', { name }),
    // ADR-052 §5 — the "needs scope grant" chip, shown beside an assignee whose own
    // scope doesn't yet reach this scope entity (rendered when needsScope).
    needsScope: { chip: tc('chain.needsScope.chip') },
  };
}

export function coverageLabels(t: TFunction): ResponsibilityCoverageLabels {
  return {
    people: (n) => t('coverage.people', { count: n }),
    requiredMissing: (n) => t('coverage.requiredMissing', { count: n }),
  };
}

/** The read row's words — `common:chains.*` (one block for every level's section and drawer). */
export function assignmentRowLabels(tc: TFunction): ResponsibilityAssignmentRowLabels {
  return {
    required: tc('chains.required'),
    notAssigned: tc('chains.notAssigned'),
  };
}

// ── coverage from the chains (the peek/detail header — mirrors the backend's
// countAssignmentsForScopes, so the inline count agrees with the list cell) ─────
export function coverageOf(chains: ScopeChain[]): { people: number; requiredMissing: number } {
  const people = new Set(chains.flatMap((c) => c.member_ids)).size;
  const requiredMissing = chains.filter((c) => c.is_required && c.member_ids.length === 0).length;
  return { people, requiredMissing };
}

// ── chains → the read list's items ──────────────────────────────────────────────
// `nameOf` resolves a member id → display name from the person lookup's rows of
// given ids the page asked (the wire carries only ordered ids, ADR-026 §C).
export function toAssignmentItems(
  chains: ScopeChain[],
  nameOf: (userId: string) => string,
): ResponsibilityAssignmentItem[] {
  return chains.map((c) => ({
    id: String(c.responsibility_id),
    name: c.name,
    required: c.is_required,
    // Full ordered assignee list (position 1 = lead) — the read row lists each on
    // its own line rather than "lead · +N others"; the id rides along so the host
    // may render the person as a reference.
    members: c.member_ids.map((id) => ({ id, name: nameOf(id) })),
  }));
}
